// Lightweight resource hooks for the Team Planner. Wraps the REST api
// in a useState/useEffect cache plus a refresh counter so a mutation
// can force a re-fetch. Avoids pulling in @tanstack/react-query for a
// single page; the rest of the BSP Hub uses the same flat fetch
// pattern (see pipe-takeoff/ProjectList).
import { useCallback, useEffect, useState } from "react";

export interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useResource<T>(
  load: () => Promise<T>,
  deps: unknown[],
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    load()
      .then((v) => {
        if (alive) {
          setData(v);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (alive) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, refresh };
}
